/**
 * Weather: the pure parts. What to ask Open-Meteo for, how to read what comes
 * back, and how to say how old a cached reading is.
 *
 * The screen shows the last cached forecast with its age when there is no
 * signal, and never a blank. Storage lives in weatherStore.ts.
 */

/** Approximate coordinates of Isla Solarte. Phase 3 replaces this with the island's own feed. */
export const ISLAND_LAT = 9.28;
export const ISLAND_LON = -82.2;

/** A reading older than this is offered as old rather than as current. */
export const STALE_AFTER_MS = 3 * 60 * 60 * 1000;

export function forecastUrl(): string {
  const params = new URLSearchParams({
    latitude: String(ISLAND_LAT),
    longitude: String(ISLAND_LON),
    current: 'temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,wind_gusts_10m,precipitation',
    daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum',
    timezone: 'America/Panama',
    forecast_days: '3',
    wind_speed_unit: 'kmh',
  });
  return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
}

export type WeatherKind =
  | 'clear'
  | 'partly'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'showers'
  | 'thunder'
  | 'unknown';

/** WMO weather interpretation codes, grouped into the handful a guest cares about. */
export function weatherKind(code: number | null | undefined): WeatherKind {
  if (code === null || code === undefined || Number.isNaN(code)) return 'unknown';
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if ((code >= 61 && code <= 67) || (code >= 71 && code <= 77)) return 'rain';
  if (code >= 80 && code <= 86) return 'showers';
  if (code >= 95 && code <= 99) return 'thunder';
  return 'unknown';
}

export type ForecastDay = {
  date: string;
  kind: WeatherKind;
  highC: number | null;
  lowC: number | null;
  rainChancePct: number | null;
  rainMm: number | null;
};

export type Forecast = {
  now: {
    tempC: number | null;
    humidityPct: number | null;
    kind: WeatherKind;
    windKph: number | null;
    gustKph: number | null;
    rainMm: number | null;
  };
  days: ForecastDay[];
};

type OpenMeteo = {
  current?: Record<string, number | null | undefined>;
  daily?: Record<string, Array<number | string | null> | undefined>;
};

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Reads the Open-Meteo response. Returns null when it is not one, so a bad payload is never cached. */
export function parseForecast(raw: unknown): Forecast | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as OpenMeteo;
  const current = data.current;
  const daily = data.daily;
  const times = daily?.time;
  if (!current || !daily || !Array.isArray(times) || times.length === 0) return null;

  const days: ForecastDay[] = times.slice(0, 3).map((date, i) => ({
    date: String(date),
    kind: weatherKind(num(daily.weather_code?.[i])),
    highC: num(daily.temperature_2m_max?.[i]),
    lowC: num(daily.temperature_2m_min?.[i]),
    rainChancePct: num(daily.precipitation_probability_max?.[i]),
    rainMm: num(daily.precipitation_sum?.[i]),
  }));

  return {
    now: {
      tempC: num(current.temperature_2m),
      humidityPct: num(current.relative_humidity_2m),
      kind: weatherKind(num(current.weather_code)),
      windKph: num(current.wind_speed_10m),
      gustKph: num(current.wind_gusts_10m),
      rainMm: num(current.precipitation),
    },
    days,
  };
}

export type CacheAge = {
  unit: 'now' | 'minutes' | 'hours' | 'days';
  value: number;
  stale: boolean;
};

/** How old a cached reading is, in the largest unit that reads naturally. Never negative. */
export function cacheAge(fetchedAt: number, now: number = Date.now()): CacheAge {
  const ms = Math.max(0, now - fetchedAt);
  const minutes = Math.floor(ms / 60_000);
  const stale = ms >= STALE_AFTER_MS;
  if (minutes < 2) return { unit: 'now', value: 0, stale };
  if (minutes < 60) return { unit: 'minutes', value: minutes, stale };
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return { unit: 'hours', value: hours, stale };
  return { unit: 'days', value: Math.floor(hours / 24), stale };
}

/** True when the sky calls for the lightning rule to be said out loud. */
export function needsLightningNote(forecast: Forecast): boolean {
  return forecast.now.kind === 'thunder' || forecast.days.some((d) => d.kind === 'thunder');
}
